import { RowCrudStatus } from "../../common/types";
import {
  PaymentReceipt,
  PaymentReceiptCreateMutationVariables,
  PaymentReceiptUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import {
  getFileVariableValue,
  getNotNullFileVariableValue,
} from "../../common/utils";
import { paymentReceiptTypeDisplayId } from "../../paymentReceiptType/store/PaymentReceiptTypeCommonStore";
import { PaymentReceiptStatus } from "../../../api/graphql";
import { signedEditionDisplayId } from "../../signedEdition/store/SignedEditionCommonStore";
import {
  PaymentReceiptUseValuesType,
  getPaymentReceiptUseValues,
} from "../../paymentReceiptUse/store/PaymentReceiptUseCommonStore";

let uniqueCounter = 1;
export type PaymentReceiptValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  paymentReceipt: string;
  paymentReceipt_data: FileCRUDType;
  __paymentReceipt_files: FileCRUDType[];
  crc: string;
  __type: string;
  showType: { displayId: string; id: string } | null;
  amount: number;
  issueDateTime: Date | null;
  status: PaymentReceiptStatus;
  totalUses: number;
  remainingUses: number;
  usedDate: Date | null;
  __signedEdition: string;
  showSignedEdition: { displayId: string; id: string } | null;
  showUses: PaymentReceiptUseValuesType[];
};

export type PaymentReceiptErrorsType = {
  paymentReceipt?: { message: string; fieldLabel: string };
  crc?: { message: string; fieldLabel: string };
  type?: { message: string; fieldLabel: string };
  amount?: { message: string; fieldLabel: string };
  issueDateTime?: { message: string; fieldLabel: string };
  status?: { message: string; fieldLabel: string };
  totalUses?: { message: string; fieldLabel: string };
  remainingUses?: { message: string; fieldLabel: string };
  usedDate?: { message: string; fieldLabel: string };
  signedEdition?: { message: string; fieldLabel: string };
  uses?: { message: string; fieldLabel: string };
};

export function PaymentReceiptValuesInit(): PaymentReceiptValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    paymentReceipt: "",
    paymentReceipt_data: fileCRUDInit(),
    __paymentReceipt_files: [],
    crc: "",
    __type: "",
    showType: null,
    amount: 0,
    issueDateTime: null,
    status: "available" as PaymentReceiptStatus,
    totalUses: 0,
    remainingUses: 0,
    usedDate: null,
    __signedEdition: "",
    showSignedEdition: null,
    showUses: [],
  };
}

export type PaymentReceiptType = PaymentReceipt | PaymentReceiptValuesType;

export function paymentReceiptDisplayId(
  source: Partial<PaymentReceipt>,
  _langStore: LangStore
) {
  return source.crc ?? _langStore.emptyStringSymbol;
}

export function getPaymentReceiptValues(
  source: PaymentReceiptType,
  _langStore: LangStore,
  _duplicated: boolean = false
): PaymentReceiptValuesType {
  const entityValues = PaymentReceiptValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : paymentReceiptDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.__paymentReceipt_files =
    source.paymentReceipt_data && !_duplicated
      ? [
          {
            id:
              "id" in source.paymentReceipt_data &&
              source.paymentReceipt_data.id
                ? source.paymentReceipt_data.id
                : `generated_${uniqueCounter++}`,
            name: source.paymentReceipt_data.name,
            description: source.paymentReceipt_data.description || undefined,
            size: source.paymentReceipt_data.size || 0,
            formattedSize: source.paymentReceipt_data.size
              ? formatBytes(source.paymentReceipt_data.size)
              : "",
            mimetype: source.paymentReceipt_data.mimetype,
            url: source.paymentReceipt_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.paymentReceipt =
    source.paymentReceipt && !_duplicated ? source.paymentReceipt : "";
  entityValues.paymentReceipt_data =
    source.paymentReceipt_data && "__status__" in source.paymentReceipt_data
      ? source.paymentReceipt_data
      : source.paymentReceipt_data
      ? {
          id: ("id" in source.paymentReceipt_data &&
          source.paymentReceipt_data.id
            ? source.paymentReceipt_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.paymentReceipt_data.name,
          description: source.paymentReceipt_data.description || undefined,
          size: source.paymentReceipt_data.size || 0,
          formattedSize: source.paymentReceipt_data.size
            ? formatBytes(source.paymentReceipt_data.size)
            : "",
          mimetype: source.paymentReceipt_data.mimetype,
          url: source.paymentReceipt_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.crc = source.crc;
  entityValues.showType =
    "type" in source && source.type
      ? {
          id: source.type.id,
          displayId: paymentReceiptTypeDisplayId(source.type, _langStore),
        }
      : "showType" in source && source.showType
      ? { id: source.showType.id, displayId: source.showType.displayId }
      : null;
  entityValues.__type = entityValues.showType?.displayId || "";
  entityValues.amount = Number(source.amount) || 0;
  entityValues.issueDateTime = source.issueDateTime
    ? new Date(source.issueDateTime)
    : null;
  entityValues.status = source.status;
  entityValues.totalUses = source.totalUses || 0;
  entityValues.remainingUses = source.remainingUses || 0;
  entityValues.usedDate = source.usedDate ? new Date(source.usedDate) : null;
  entityValues.showSignedEdition =
    "signedEdition" in source && source.signedEdition
      ? {
          id: source.signedEdition.id,
          displayId: signedEditionDisplayId(source.signedEdition, _langStore),
        }
      : "showSignedEdition" in source && source.showSignedEdition
      ? {
          id: source.showSignedEdition.id,
          displayId: source.showSignedEdition.displayId,
        }
      : null;
  entityValues.__signedEdition =
    entityValues.showSignedEdition?.displayId || "";
  entityValues.showUses =
    "uses" in source && source.uses
      ? (source.uses || []).map((x) => {
          const row: PaymentReceiptUseValuesType = getPaymentReceiptUseValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showUses" in source && source.showUses
      ? (source.showUses || []).map((x) => {
          const row: PaymentReceiptUseValuesType = getPaymentReceiptUseValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getPaymentReceiptUpdateVariables(
  values: PaymentReceiptValuesType
): PaymentReceiptUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    paymentReceipt: getFileVariableValue(values.__paymentReceipt_files),
    issueDateTime: values.issueDateTime?.toISOString() || null,
  };
}

export function getPaymentReceiptCreateVariables(
  values: PaymentReceiptValuesType
): PaymentReceiptCreateMutationVariables {
  return {
    paymentReceipt: getNotNullFileVariableValue(values.__paymentReceipt_files),
    typeId: values.showType?.id || "",
    amount: values.amount,
    issueDateTime: values.issueDateTime?.toISOString() || null,
  };
}

export function getPaymentReceiptBatchCreateVariables(
  values: PaymentReceiptValuesType
): any {
  return {
    typeId: values.showType?.id || "",
    amount: values.amount,
    issueDateTime: values.issueDateTime?.toISOString() || null,
  };
}
